import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { Bell, Check, CheckCheck, CreditCard, ShieldAlert, X, FileText, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { ThreeDSChallengePopup } from './ThreeDSOtpPopup';
import { useToast } from '@/hooks/use-toast';

const typeIcons: Record<string, typeof Bell> = {
  '3ds_challenge': ShieldAlert,
  card_event: CreditCard,
  alert: ShieldAlert,
  compliance_request: FileText,
  message: MessageSquare,
  info: Bell,
};

export function NotificationCenter() {
  const { profile } = useCustomerAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [activeChallenge, setActiveChallenge] = useState<{ id: string; metadata: any } | null>(null);

  const { data: notifications = [] } = useQuery({
    queryKey: ['customer-notifications', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase
        .from('customer_notifications')
        .select('*')
        .eq('client_id', profile.id)   // explicit filter — don't rely on my_client_id() LIMIT 1
        .eq('dismissed', false)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
    enabled: !!profile?.id,
    refetchInterval: 30000,
  });

  // Realtime subscription
  useEffect(() => {
    if (!profile?.id) return;
    const channel = supabase
      .channel('customer-notifications-rt')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'customer_notifications',
        filter: `client_id=eq.${profile.id}`,
      }, (payload) => {
        queryClient.invalidateQueries({ queryKey: ['customer-notifications', profile.id] });
        const n = payload.new as any;
        if (n.type === '3ds_challenge') {
          setActiveChallenge({ id: n.id, metadata: n.metadata });
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [profile?.id, queryClient]);

  // Check for unread 3DS challenge on load
  useEffect(() => {
    const challengeNotif = notifications.find((n: any) => n.type === '3ds_challenge' && !n.read);
    if (challengeNotif && !activeChallenge) {
      setActiveChallenge({ id: challengeNotif.id, metadata: challengeNotif.metadata as any });
    }
  }, [notifications, activeChallenge]);

  const unreadCount = notifications.filter((n: any) => !n.read).length;

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('customer_notifications').update({ read: true }).eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-notifications', profile?.id] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const ids = notifications.filter((n: any) => !n.read).map((n: any) => n.id);
      if (ids.length === 0) return;
      await supabase.from('customer_notifications').update({ read: true }).in('id', ids);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-notifications', profile?.id] }),
  });

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('customer_notifications').update({ dismissed: true, read: true }).eq('id', id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-notifications', profile?.id] }),
  });

  const handleChallengeAction = (action: 'approve' | 'deny') => {
    if (!activeChallenge) return;
    markRead.mutate(activeChallenge.id);
    toast({
      title: action === 'approve' ? 'Transaction Approved' : 'Transaction Denied',
      description: action === 'approve'
        ? 'Your 3D Secure response has been recorded. The merchant will process your transaction.'
        : 'The transaction has been denied. Your card will not be charged.',
    });
    setActiveChallenge(null);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative h-8 w-8 rounded-lg">
            <Bell className={`w-4 h-4 transition-colors ${unreadCount > 0 ? 'text-foreground' : 'text-muted-foreground'}`} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-black flex items-center justify-center px-1 shadow-lg shadow-destructive/40 ring-2 ring-background animate-in zoom-in-75 duration-200">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-bold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => markAllRead.mutate()}>
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </Button>
            )}
          </div>
          <ScrollArea className="max-h-80">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-xs">
                No notifications yet
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((n: any) => {
                  const Icon = typeIcons[n.type] || Bell;
                  return (
                    <div
                      key={n.id}
                      className={`px-4 py-3 flex gap-3 items-start hover:bg-muted/30 transition-colors cursor-pointer ${!n.read ? 'bg-accent/5' : ''}`}
                      onClick={() => {
                        if (!n.read) markRead.mutate(n.id);
                        if (n.type === '3ds_challenge') {
                          setActiveChallenge({ id: n.id, metadata: n.metadata });
                          setOpen(false);
                        } else if (n.type === 'compliance_request') {
                          setOpen(false);
                          navigate('/app/support');
                        } else if (n.type === 'message') {
                          setOpen(false);
                          const link = (n.metadata as any)?.link;
                          // Fix legacy /app/requests links to point to unified support hub
                          const resolved = link === '/app/requests' ? '/app/support' : (link || '/app/support');
                          navigate(resolved);
                        } else if ((n.metadata as any)?.link) {
                          setOpen(false);
                          navigate((n.metadata as any).link);
                        }
                      }}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.type === '3ds_challenge' ? 'bg-destructive/15 text-destructive' : 'bg-accent/15 text-accent'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1">
                          <p className={`text-xs leading-tight ${!n.read ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                            {n.title}
                          </p>
                          <Button
                            variant="ghost" size="icon" className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); dismiss.mutate(n.id); }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        {n.body && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                        <p className="text-[10px] text-muted-foreground/70 mt-1">
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      {!n.read && <div className="w-2 h-2 rounded-full bg-accent shrink-0 mt-1.5" />}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* 3DS Challenge Pop-up */}
      {activeChallenge && (
        <ThreeDSChallengePopup
          merchant={activeChallenge.metadata?.merchant_name || 'Online Merchant'}
          amount={activeChallenge.metadata?.amount || '€0.00'}
          challengeId={activeChallenge.metadata?.challenge_id}
          onApprove={() => handleChallengeAction('approve')}
          onDeny={() => handleChallengeAction('deny')}
          onDismiss={() => {
            markRead.mutate(activeChallenge.id);
            setActiveChallenge(null);
          }}
        />
      )}
    </>
  );
}
